/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useRef, useState } from 'react';
import { Box, Grid2, Skeleton, Typography } from '@mui/material';
import {
  Portlet,
  PortletContent,
  PortletHeader,
  PortletLabel,
  PortletToolbar,
} from 'components';
import { useIntl } from 'react-intl';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

import palette from 'theme/customColors';
import { Download, ShareButton } from 'components/Download';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Componente que muestra una gráfica de barras de consumo energético.
 *
 * @param {object} props - Propiedades del componente.
 * @param {string} props.title - Título de la gráfica.
 * @param {Array<number>|number} props.totalConsumeskW - Datos de consumo en kW. Si `custom` es 1, es un número; si no, es un array de tres valores.
 * @param {Array<number>|number} props.totalConsumeskWh - Datos de consumo en kWh. Si `custom` es 1, es un número; si no, es un array de tres valores.
 * @param {number} [props.custom] - Si es 1, la gráfica mostrará solo un dato en lugar de tres.
 * @returns {JSX.Element} Componente de gráfica de barras.
 */
export default function BarChart(props) {
  const { title, totalConsumeskW = [], totalConsumeskWh = [], custom } = props;
  const intl = useIntl();
  const chartRef = useRef();
  const [iconHide, setIconHide] = useState(false);

  // Verifica si el prop "custom" es 1 para modificar la estructura de datos
  const isCustom = custom === 1;

  /**
   * Etiquetas del eje X de la gráfica.
   * Si `custom === 1`, solo se muestra "Total".
   * Si no, se muestran tres categorías: "Base", "Intermedio" y "Punta".
   */
  const labels = isCustom
    ? [[intl.formatMessage({ id: 'app.charge.board.demandAndConsump.title' })]]
    : [
        intl.formatMessage({
          id: 'app.meter.board.demandAndConsump.dataSet.base',
        }),
        intl.formatMessage({
          id: 'app.meter.board.demandAndConsump.dataSet.interm',
        }),
        intl.formatMessage({
          id: 'app.meter.board.demandAndConsump.dataSet.tip',
        }),
      ];

  /**
   * Datos que se mostrarán en la gráfica.
   * Se ajusta la estructura de los datos en función de la prop `custom`.
   */
  const data = {
    labels,
    datasets: [
      {
        label: ' kW',
        yAxisID: 'A',
        backgroundColor: isCustom
          ? ['#A53693']
          : ['#8CBA42', '#ebc057', '#ED685E'],
        data: isCustom
          ? [parseFloat(totalConsumeskW)]
          : totalConsumeskW.map(parseFloat),
        borderRadius: 10,
      },
      {
        label: ' kWh',
        yAxisID: 'B',
        backgroundColor: isCustom ? ['#00AFEF'] : ['#3A5F0B', '#FFCC00', 'red'],
        data: isCustom
          ? [parseFloat(totalConsumeskWh)]
          : totalConsumeskWh.map(parseFloat),
        borderRadius: 10,
      },
    ],
  };

  /**
   * Configuración de la gráfica de barras.
   * Se habilita la leyenda y se ajustan estilos para mejor visualización.
   */
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: palette.common.white,
        titleColor: palette.text.primary,
        bodyColor: palette.text.secondary,
        borderColor: palette.border,
        borderWidth: 1,
      },
    },
    layout: {
      padding: { top: 20 },
    },
    scales: {
      x: {
        ticks: {
          color: palette.text.secondary,
          font: { size: 14 },
          padding: 20,
        },
        grid: {
          borderDash: [5, 5],
          color: palette.divider,
        },
      },
      A: {
        position: 'left',
        ticks: {
          color: '#730065',
          beginAtZero: true,
          callback: (value) => value.toLocaleString(),
        },
        grid: {
          borderDash: [5, 5],
          color: palette.divider,
        },
      },
      B: {
        position: 'right',
        ticks: {
          color: '#0080BC',
          beginAtZero: true,
          callback: (value) => value.toLocaleString(),
        },
        grid: {
          borderDash: [5, 5],
          color: palette.divider,
        },
      },
    },
  };

  /**
   * Valida si hay datos disponibles.
   * - En modo "custom", los valores deben ser números definidos.
   * - En modo normal, debe haber tres valores en cada array.
   */
  const hasData = isCustom
    ? totalConsumeskW !== undefined && totalConsumeskWh !== undefined
    : totalConsumeskW.length === 3 && totalConsumeskWh.length === 3;

  return (
    <div ref={chartRef} style={{ height: '100%' }}>
      <Portlet style={{ height: '100%' }}>
        <PortletHeader noDivider={true}>
          {title ? (
            <PortletLabel title={title} />
          ) : (
            <Skeleton variant="text" width={200} height={50} />
          )}
          <PortletToolbar style={{ display: 'flex', gap: 8 }}>
            <Download itemRef={chartRef} name={title} />
            <ShareButton itemRef={chartRef} name={title} />
          </PortletToolbar>
        </PortletHeader>
        <PortletContent>
          {hasData ? (
            <Box height={400} position="relative">
              <Bar data={data} options={options} />
              <Grid2
                container={true}
                spacing={2}
                position="relative"
                display="flex"
                bottom="17%"
                mt={1}
                justifyContent="space-between"
                flexDirection="row"
              >
                {isCustom ? (
                  <Grid2
                    size={12}
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-evenly"
                  >
                    <Typography>
                      {intl.formatMessage({
                        id: 'app.group.board.mainHead.maxDemand',
                      })}
                    </Typography>
                    <Typography>
                      {intl.formatMessage({
                        id: 'app.charge.board.highConsumDayCurrMonth.consumption',
                      })}
                    </Typography>
                  </Grid2>
                ) : (
                  <>
                    <Grid2
                      size={4}
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-evenly"
                      pl="3rem"
                    >
                      <Typography>kW</Typography>
                      <Typography>kWh</Typography>
                    </Grid2>
                    <Grid2
                      size={4}
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-evenly"
                    >
                      <Typography>kW</Typography>
                      <Typography>kWh</Typography>
                    </Grid2>
                    <Grid2
                      size={4}
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-evenly"
                      pr="4rem"
                    >
                      <Typography>kW</Typography>
                      <Typography>kWh</Typography>
                    </Grid2>
                  </>
                )}
              </Grid2>
            </Box>
          ) : (
            <Skeleton variant="rectangular" height={400} />
          )}
        </PortletContent>
      </Portlet>
    </div>
  );
}
